import { ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, inject, Output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { heroEllipsisHorizontal, heroMagnifyingGlass, heroPlus, heroTag } from '@ng-icons/heroicons/outline';
import { NgpMenuTrigger } from 'ng-primitives/menu';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { HeaderSearchService } from '../../../../core/search/header-search.service';
import { ConversationFiltersStore } from '../../data/conversation-filters.store';
import { ConversationsStore } from '../../data/conversations.store';
import { conversationPriorityLabel, conversationStatusLabel } from '../../i18n/conversation-labels';
import type { ConversationView } from '../../models/conversation-view.types';
import {
  UiBadge,
  UiButton,
  UiChannelIcon,
  UiEmptyState,
  UiHint,
  UiIconButton,
  UiInput,
  UiLabel,
  UiMenuItem,
  UiMenuPanel,
  UiPulseDot,
  UiRecordRowButton,
  UiSkeletonGrid,
} from '../../../../shared/ui';
import { ContactSearchDialogComponent } from '../iniciar-conversa/contact-search-dialog';
import { IniciarConversaDialogComponent } from '../iniciar-conversa/iniciar-conversa-dialog';

const SEARCH_DEBOUNCE_MS = 250;
type ConversationBadgeTone = 'warning' | 'danger' | 'neutral' | 'accent';

/** Inbox conversation list with debounced search. Reads the filtered list and drives selection. */
@Component({
  selector: 'app-conversation-list',
  imports: [
    NgIcon,
    NgpMenuTrigger,
    UiBadge,
    UiButton,
    UiIconButton,
    UiInput,
    UiMenuItem,
    UiMenuPanel,
    UiPulseDot,
    UiRecordRowButton,
    UiChannelIcon,
    UiEmptyState,
    UiHint,
    UiLabel,
    UiSkeletonGrid,
    ContactSearchDialogComponent,
    IniciarConversaDialogComponent,
  ],
  providers: [provideIcons({ heroTag, heroEllipsisHorizontal, heroMagnifyingGlass, heroPlus })],
  templateUrl: './conversation-list.html',
  styleUrls: ['./conversation-list.css', '../conversation-atoms.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConversationListComponent {
  @Output() readonly conversationSelected = new EventEmitter<string>();
  protected readonly store = inject(ConversationsStore);
  protected readonly filters = inject(ConversationFiltersStore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly headerSearch = inject(HeaderSearchService);
  protected readonly searchValue = signal('');
  /** "Nova conversa" flow: search a contact, then pick a startable channel. */
  protected readonly contactSearchOpen = signal(false);
  protected readonly startForContactId = signal<string | null>(null);

  private readonly searchChanges = new Subject<string>();

  constructor() {
    this.headerSearch.register(
      {
        id: 'conversations.search',
        label: 'Buscar conversas',
        placeholder: 'Buscar conversas',
        value: this.searchValue,
        setValue: (value) => this.onSearchInput(value),
        clear: () => this.onSearchInput(''),
      },
      this.destroyRef,
    );

    this.searchChanges
      .pipe(debounceTime(SEARCH_DEBOUNCE_MS), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => this.filters.applySearch(value));
  }

  onSearchInput(value: string): void {
    this.searchValue.set(value);
    this.searchChanges.next(value);
  }

  selectConversation(conversationId: string): void {
    this.store.selectConversation(conversationId);
    this.conversationSelected.emit(conversationId);
  }

  /** Contact chosen in the search dialog → advance to the startable-channels selector. */
  protected onContactPicked(contactId: string): void {
    this.contactSearchOpen.set(false);
    this.startForContactId.set(contactId);
  }

  protected onMenuMarkAsUnread(id: string, event: MouseEvent): void {
    event.stopPropagation();
    this.store.markAsUnread(id);
  }

  protected onMenuMarkAsRead(id: string, event: MouseEvent): void {
    event.stopPropagation();
    this.store.markAsReadFromMenu(id);
  }

  protected statusLabel(status: ConversationView['status']): string {
    return conversationStatusLabel(status);
  }

  protected priorityLabel(priority: ConversationView['priority']): string {
    return conversationPriorityLabel(priority);
  }

  protected priorityTone(priority: ConversationView['priority']): ConversationBadgeTone {
    if (priority === 'urgent') return 'danger';
    if (priority === 'high') return 'warning';
    if (priority === 'normal') return 'accent';
    return 'neutral';
  }
}
